import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X, Loader2, Download, FileAudio, Mic, FileText,
    CheckCircle, AlertCircle, Clock
} from 'lucide-react';

/**
 * TranscriptionProgressOverlay - Centered floating progress overlay
 * 
 * Features:
 * - Centered in viewport with semi-transparent backdrop
 * - Detailed progress stages with smooth animations
 * - Elapsed time tracking
 * - Cancel button for long-running transcriptions
 * - Uses React Portal for proper positioning
 */
const TranscriptionProgressOverlay = ({
    isOpen,
    progress = 0,
    stage = 'initializing',
    message,
    onCancel,
    startTime
}) => {
    const [elapsedTime, setElapsedTime] = useState(0);

    // Prevent body scroll when overlay is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Update elapsed time every second
    useEffect(() => {
        if (!isOpen || !startTime) return;

        const interval = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            setElapsedTime(elapsed);
        }, 1000);

        return () => clearInterval(interval);
    }, [isOpen, startTime]);

    // Reset elapsed time when modal opens
    useEffect(() => {
        if (isOpen) {
            setElapsedTime(0);
        }
    }, [isOpen]);

    // Format elapsed time
    const formatTime = useCallback((seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }, []);

    // Stage configuration
    const stageConfig = useMemo(() => ({
        initializing: {
            icon: Loader2,
            label: 'Initializing',
            color: 'text-slate-500',
            bg: 'bg-slate-100',
            animate: true
        },
        downloading: {
            icon: Download,
            label: 'Downloading',
            color: 'text-blue-500',
            bg: 'bg-blue-100',
            animate: true
        },
        extracting: {
            icon: FileAudio,
            label: 'Extracting Audio',
            color: 'text-purple-500',
            bg: 'bg-purple-100',
            animate: true
        },
        uploading: {
            icon: Upload,
            label: 'Uploading',
            color: 'text-indigo-500',
            bg: 'bg-indigo-100',
            animate: true
        },
        transcribing: {
            icon: Mic,
            label: 'Transcribing',
            color: 'text-amber-500',
            bg: 'bg-amber-100',
            animate: true
        },
        processing: {
            icon: FileText,
            label: 'Processing',
            color: 'text-teal-500',
            bg: 'bg-teal-100',
            animate: true
        },
        complete: {
            icon: CheckCircle,
            label: 'Complete',
            color: 'text-green-500',
            bg: 'bg-green-100',
            animate: false
        },
        error: {
            icon: AlertCircle,
            label: 'Error',
            color: 'text-red-500',
            bg: 'bg-red-100',
            animate: false
        }
    }), []);

    const currentStage = stageConfig[stage] || stageConfig.initializing;
    const StageIcon = currentStage.icon;

    // Progress stages for visual indicator
    const progressStages = useMemo(() => [
        { id: 'downloading', threshold: 10 },
        { id: 'extracting', threshold: 30 },
        { id: 'uploading', threshold: 40 },
        { id: 'transcribing', threshold: 90 },
        { id: 'complete', threshold: 100 }
    ], []);

    // Get current stage index
    const currentStageIndex = useMemo(() => {
        return progressStages.findIndex(s => progress < s.threshold);
    }, [progress, progressStages]);

    // Overlay content
    const overlayContent = (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm"
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        className="bg-white rounded-3xl shadow-2xl overflow-hidden w-full max-w-md mx-4"
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`w-12 h-12 rounded-xl ${currentStage.bg} flex items-center justify-center`}>
                                        <StageIcon
                                            size={24}
                                            className={`${currentStage.color} ${currentStage.animate ? 'animate-spin' : ''}`}
                                        />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold font-['Outfit']">
                                            Transcribing
                                        </h3>
                                        <p className="text-sm text-indigo-200">
                                            {currentStage.label}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={onCancel}
                                    className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Progress Content */}
                        <div className="p-6">
                            {/* Progress Bar */}
                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-slate-600">Progress</span>
                                    <span className="text-sm font-bold text-indigo-600">{Math.round(progress)}%</span>
                                </div>
                                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                                    <motion.div
                                        className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${progress}%` }}
                                        transition={{ duration: 0.3, ease: "easeOut" }}
                                    />
                                </div>
                            </div>

                            {/* Stage Indicators */}
                            <div className="flex items-center justify-between mb-6 px-2">
                                {progressStages.map((s, index) => {
                                    const isComplete = progress >= s.threshold;
                                    const isCurrent = index === currentStageIndex;
                                    const stageInfo = stageConfig[s.id];

                                    return (
                                        <div key={s.id} className="flex flex-col items-center">
                                            <div
                                                className={`
                                                    w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300
                                                    ${isComplete
                                                        ? 'bg-green-500 text-white'
                                                        : isCurrent
                                                            ? `${stageInfo?.bg || 'bg-slate-100'} ${stageInfo?.color || 'text-slate-500'}`
                                                            : 'bg-slate-100 text-slate-300'}
                                                `}
                                            >
                                                {isComplete ? (
                                                    <CheckCircle size={16} />
                                                ) : (
                                                    <div className="w-2 h-2 rounded-full bg-current" />
                                                )}
                                            </div>
                                            <span className={`
                                                text-[10px] mt-1 font-medium capitalize
                                                ${isComplete || isCurrent ? 'text-slate-600' : 'text-slate-300'}
                                            `}>
                                                {s.id}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Status Message */}
                            {message && (
                                <div className="bg-slate-50 rounded-xl p-4 mb-4">
                                    <p className="text-sm text-slate-600 text-center">
                                        {message}
                                    </p>
                                </div>
                            )}

                            {/* Time and Cancel */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                    <Clock size={14} />
                                    <span>Elapsed: {formatTime(elapsedTime)}</span>
                                </div>
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={onCancel}
                                    className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl text-sm font-bold transition-colors"
                                >
                                    Cancel
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    // Render using portal to body
    return createPortal(overlayContent, document.body);
};

// Upload icon component
const Upload = ({ size, className }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
);

export default TranscriptionProgressOverlay;
